import React, { useEffect, useRef, useState } from 'react';
import Button from '@cvent/carina/components/Button';
import { Tooltip } from '@cvent/carina/components/Tooltip';
import Link from 'next/link';
import type { CarinaButtonProps } from '@cvent/carina/components/Button';

interface Props extends CarinaButtonProps {
  /** text of tooltip and button */
  text: string;
  /** href if button is a link */
  href?: string;
}

/**
 * A button that displays a tooltip if text overflows (offsetWidth < scrollWidth).
 * This is useful if the button has a max width and text might be truncated (contain ellipsis)
 * Carina V1 buttons do not support multi-line buttons and recommends using a tooltip to display full text
 * @returns
 */
function ButtonWithOverflowTooltip({ text, href, ...rest }: Props): JSX.Element {
  const buttonRef = useRef(null);
  const [displayTooltip, setDisplayTooltip] = useState(false);

  useEffect(() => {
    if (buttonRef.current) {
      const spanElement = buttonRef.current.querySelector('span');
      setDisplayTooltip(spanElement.offsetWidth < spanElement.scrollWidth);
    }
  }, []);

  const button = href ? (
    <div>
      <Link href={href} passHref legacyBehavior>
        <Button forwardedRef={buttonRef} text={text} element="a" {...rest} />
      </Link>
    </div>
  ) : (
    <Button forwardedRef={buttonRef} text={text} {...rest} />
  );

  return displayTooltip ? (
    <Tooltip text={text} accessibilityLabel={text} preventOverflow={false} trigger={button} testID="tooltip" />
  ) : (
    button
  );
}

export default ButtonWithOverflowTooltip;
